import type { PropsWithChildren } from 'react'

import { useQuery } from '@fv/client-core'

import { inProcessStatusOptions } from '@/components/CustomFilterForm/filterOptions'
import type { FilterConfig } from '@/components/CustomFilterForm/types'
import {
  getFilterQueryString,
  type SavedFilterGroup,
} from '@/types/SavedFilter'

import { useAccountFeatures, useRole } from '../auth/hooks'
import {
  fetchInProcessShipments,
  inProcessShipmentKeys,
} from '../shipment-list/hooks/useInProcessShipments'
import { getFilters } from './filterRegistry'
import { useWorkflowFilterLists } from './hooks'
import {
  bookingErrorsFilter,
  exhaustedRouteGuidesFilter,
  rejectedTendersFilter,
  useLtlGroupFilter,
  useParcelGroupFilter,
  useTruckloadGroupFilter,
} from './in-process-filters'
import { ShipmentFilterListProvider } from './ShipmentFilterListProvider'
import { allFilter, vendorCreatedFilter } from './shared-filters'
import { useHiddenViews, useSavedViews } from './useSavedViews'

export const InProcessFiltersProvider = ({ children }: PropsWithChildren) => {
  const filters = useInProcessFilters()
  const filterConfig = useInProcessFilterConfig()
  return (
    <ShipmentFilterListProvider
      state={{
        listType: 'inProcessList',
        filters,
        filterConfig,
      }}
    >
      {children}
    </ShipmentFilterListProvider>
  )
}

const useInProcessFilters = () => {
  const hiddenViews = useHiddenViews()
  const savedViews = useSavedViews('inProcessList')
  const bookingErrors = useBookingErrors()
  const tenderRejections = useTenderRejections()
  const exhaustedRouteGuides = useExhaustedRouteGuides()
  const truckloadGroupFilter = useTruckloadGroupFilter()
  const parcelGroupFilter = useParcelGroupFilter()
  const ltlGroupFilter = useLtlGroupFilter()
  const { vendorPortal } = useAccountFeatures()
  const role = useRole()
  const orderedFilters = useWorkflowFilterLists([
    ltlGroupFilter,
    parcelGroupFilter,
    ...(role === 'shipper' ? [truckloadGroupFilter] : []),
  ])

  const isntVendor = role !== 'vendor'
  const inProcessSystemFilters = [
    ...(bookingErrors.data?.data.length && isntVendor
      ? [bookingErrorsFilter]
      : []),
    ...(tenderRejections.data?.data.length && isntVendor
      ? [rejectedTendersFilter]
      : []),
    ...(exhaustedRouteGuides.data?.data.length && isntVendor
      ? [exhaustedRouteGuidesFilter]
      : []),
    allFilter(true),
    ...(vendorPortal && role === 'shipper' ? [vendorCreatedFilter(true)] : []),
    ...orderedFilters,
  ]
  const systemFilters =
    inProcessSystemFilters.filter(f => !hiddenViews.find(id => id === f._id)) ??
    []

  const custom: SavedFilterGroup = {
    _id: 'custom-group',
    filter: {},
    isLocked: true,
    isDisabled: true,
    icon: 'arrow-down',
    name: 'Custom filters',
    children: savedViews,
    queryString: '',
  }

  return savedViews.length ? [...systemFilters, custom] : systemFilters
}

const useBookingErrors = () => {
  const queryString = getFilterQueryString(bookingErrorsFilter)
  return useQuery(inProcessShipmentKeys.recentType('booking-error'), () =>
    fetchInProcessShipments(`${queryString}&limit=1&skip=0`),
  )
}
const useTenderRejections = () => {
  const queryString = getFilterQueryString(rejectedTendersFilter)
  return useQuery(inProcessShipmentKeys.recentType('rejected-tender'), () =>
    fetchInProcessShipments(`${queryString}&limit=1&skip=0`),
  )
}

const useExhaustedRouteGuides = () => {
  const queryString = getFilterQueryString(exhaustedRouteGuidesFilter)
  return useQuery(
    inProcessShipmentKeys.recentType('exhausted-route-guide'),
    () => fetchInProcessShipments(`${queryString}&limit=1&skip=0`),
  )
}

const useInProcessFilterConfig = (): FilterConfig[] => {
  const role = useRole()
  const { vendorPortal } = useAccountFeatures()

  return getFilters([
    'originAccount[]',
    'destAccount[]',
    'pickupDate',
    'createdDate',
    {
      label: 'Status',
      name: 'status[]',
      options: inProcessStatusOptions,
    },
    'mode[]',
    ...(role === 'shipper'
      ? ([
          'direction[]',
          'location[]',
          'isLiveLoad',
          'isSpot',
          'upload[]',
          ...(vendorPortal ? (['isVendor', 'vendors[]'] as const) : []),
          'quotedBy[]',
        ] as const)
      : []),
  ])
}
